import { useCallback } from 'react';
import { useCommitmentContext } from '../context/CommitmentContext';
import { Commitment } from '../context/CommitmentContext';

/**
 * Hook to use the Commitment Context API
 */
export const useCommitmentsContext = () => {
  const { 
    state, 
    fetchMyCommitments, 
    fetchReceivedCommitments, 
    fetchActionRequired,
    fetchAllCommitments,
    updateCommitment,
    removeCommitment,
    clearError,
    refreshAllData,
    clearAllData,
    isDataStale,
    // Filter functions
    getCommitmentsByStatus,
    getApprovedCommitments,
    getPendingCommitments,
    getRejectedCommitments,
    getPendingApprovalCommitments,
    getMyCommitmentsByStatus,
    getReceivedCommitmentsByStatus,
    getMyPendingApprovalCommitments,
    getReceivedPendingApprovalCommitments,
    getMyPromisesForActions,
    getNudgesToAnswerForActions,
    getPromisesOwedToMeForActions,
    getRequestsToCommitForActions,
    getAwaitingResponseForActions,
    getBadgeRequestsForActions,
    getPendingApprovalBadgeRequestsForActions,
    getMyPendingApprovalBadgeRequestsForActions,
    getAwaitingResponseOthersForActions,
    requestBadge,
    acceptInvitation,
    declineInvitation,
    revokeOffer,
    revokeNMOffer,
    revokeInvitation,
    revokeNMInvitation,
    issueBadge,
    rejectBadge,
    clarifyBadge,
    acceptBadgeRequest,
    declineBadgeRequest,
    answerQuestion,
    // Badge-based filtering
    getCommitmentsByBadgeName,
    getMyCommitmentsByBadgeName,
    getReceivedCommitmentsByBadgeName,
    getCommitmentsByBadgeType,
    getAllBadgeNames,
    getAllBadgeTypes,
    // Badge counting functions
    getApprovedBadgeCounts,
    getApprovedBadgeCount,
    // Computed properties
    allCommitments,
    approvedCommitments,
    pendingCommitments,
    rejectedCommitments,
    pendingApprovalCommitments,
    approvedBadgeCounts,
    fetchCommitmentDetails,
  } = useCommitmentContext();

  const fetchMyCommitmentsData = useCallback(
    async (params?: any) => {
      await fetchMyCommitments(params);
    },
    [fetchMyCommitments]
  );

  const fetchReceivedCommitmentsData = useCallback(
    async (params?: any) => {
      await fetchReceivedCommitments(params);
    },
    [fetchReceivedCommitments]
  );

  const fetchActionRequiredData = useCallback(
    async (params?: any, force?: boolean) => {
      await fetchActionRequired(params, force);
    },
    [fetchActionRequired]
  );

  const updateCommitmentData = useCallback(
    (commitment: Commitment) => {
      updateCommitment(commitment);
    },
    [updateCommitment]
  );

  const removeCommitmentData = useCallback(
    (commitmentId: string | number) => {
      removeCommitment(commitmentId);
    },
    [removeCommitment]
  );

  const clearCommitmentError = useCallback(
    (errorType: keyof typeof state.errors) => {
      clearError(errorType);
    },
    [clearError]
  );

  const issueBadgeAction = useCallback(
    async (commitmentId: string | number) => {
      await issueBadge(commitmentId);
    },
    [issueBadge]
  );

  const rejectBadgeAction = useCallback(
    async (commitmentId: string | number) => {
      await rejectBadge(commitmentId);
    },
    [rejectBadge]
  );

  const clarifyBadgeAction = useCallback(
    async (commitmentId: string | number, clarificationText: string) => {
      await clarifyBadge(commitmentId, clarificationText);
    },
    [clarifyBadge]
  );

  const acceptBadgeRequestAction = useCallback(
    async (commitmentId: string | number) => {
      await acceptBadgeRequest(commitmentId);
    },
    [acceptBadgeRequest]
  );

  const declineBadgeRequestAction = useCallback(
    async (commitmentId: string | number) => {
      await declineBadgeRequest(commitmentId);
    },
    [declineBadgeRequest]
  );

  const answerQuestionAction = useCallback(
    async (commitment: Commitment | any, answerText: string) => {
      await answerQuestion(commitment, answerText);
    },
    [answerQuestion]
  );

  const revokeInvitationAction = useCallback(
    async (invitationId: string | number) => {
      await revokeInvitation(invitationId);
    },
    [revokeInvitation]
  );

  const revokeNMInvitationAction = useCallback(
    async (invitationId: string | number) => {
      await revokeNMInvitation(invitationId);
    },
    [revokeNMInvitation]
  );

  return {
    // Data
    myCommitments: state.myCommitments,
    receivedCommitments: state.receivedCommitments,
    actionRequired: state.actionRequired,
    receivedInvitations: state.receivedInvitations,
    lastFetched: state.lastFetched,
    sentInvitations: state.sentInvitations,
    sentNMInvitations: state.sentNMInvitations,
    sentNMOffers: state.sentNMOffers,
    
    // Loading states
    loading: state.loading,
    
    // Error states
    errors: state.errors,
    
    // Actions
    fetchMyCommitments: fetchMyCommitmentsData,
    fetchReceivedCommitments: fetchReceivedCommitmentsData,
    fetchActionRequired: fetchActionRequiredData,
    fetchAllCommitments,
    updateCommitment: updateCommitmentData,
    removeCommitment: removeCommitmentData,
    clearError: clearCommitmentError,
    refreshAllData,
    clearAllData,
    
    // Utilities
    isDataStale,
    
    // Filter functions
    getCommitmentsByStatus,
    getApprovedCommitments,
    getPendingCommitments,
    getRejectedCommitments,
    getPendingApprovalCommitments,
    getMyCommitmentsByStatus,
    getReceivedCommitmentsByStatus,
    getMyPendingApprovalCommitments,
    getReceivedPendingApprovalCommitments,
    getMyPromisesForActions,
    getNudgesToAnswerForActions,
    getPromisesOwedToMeForActions,
    getRequestsToCommitForActions,
    getAwaitingResponseForActions,
    getBadgeRequestsForActions,
    getPendingApprovalBadgeRequestsForActions,
    getMyPendingApprovalBadgeRequestsForActions,
    getAwaitingResponseOthersForActions,
    requestBadge,
    acceptInvitation,
    declineInvitation,
    revokeOffer,
    revokeNMOffer,
    revokeInvitation: revokeInvitationAction,
    revokeNMInvitation: revokeNMInvitationAction,
    issueBadge: issueBadgeAction,
    rejectBadge: rejectBadgeAction,
    clarifyBadge: clarifyBadgeAction,
    acceptBadgeRequest: acceptBadgeRequestAction,
    declineBadgeRequest: declineBadgeRequestAction,
    answerQuestion: answerQuestionAction,
    
    // Badge-based filtering
    getCommitmentsByBadgeName,
    getMyCommitmentsByBadgeName,
    getReceivedCommitmentsByBadgeName,
    getCommitmentsByBadgeType,
    getAllBadgeNames,
    getAllBadgeTypes,
    
    // Badge counting functions
    getApprovedBadgeCounts,
    getApprovedBadgeCount,
    
    // Computed properties
    allCommitments,
    approvedCommitments,
    pendingCommitments,
    rejectedCommitments,
    pendingApprovalCommitments,
    approvedBadgeCounts,
    fetchCommitmentDetails,
  };
};
